import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockIcon from "@mui/icons-material/Lock";
import { subscriptionsApi } from "../../services/api/subscriptions.api";
import {
  extractApiErrorCode,
  TRIAL_CARD_ALREADY_USED_CODE,
} from "../../services/card-gate/card-gate-controller";
import { config } from "../../config/env";

const stripePromise = config.THIRD_PARTY.STRIPE_PUBLIC_KEY
  ? loadStripe(config.THIRD_PARTY.STRIPE_PUBLIC_KEY)
  : null;

interface TrialCardFormProps {
  /**
   * Вызывается после успешной серверной активации trial (карта застолблена).
   * card-gate: провайдер завершает gate → перехватчик повторяет исходный запрос;
   * standalone-экран: навигация на дашборд.
   */
  onActivated: () => void;
  /** Кнопка «не сейчас» (в модалке card-gate). Отсутствует на standalone-экране. */
  onCancel?: () => void;
}

// ─────────────────────────────────────────────
// Переиспользуемая Stripe-форма (рендерится ТОЛЬКО внутри <Elements>)
// ─────────────────────────────────────────────
function TrialCardForm({ onActivated, onCancel }: TrialCardFormProps) {
  const { t } = useTranslation("common");
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  // 409 TRIAL_CARD_ALREADY_USED — карта уже жгла trial: путь только через подписку.
  const [alreadyUsed, setAlreadyUsed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Шаг 1: clientSecret от бэкенда (SetupIntent, zero-charge).
      const { clientSecret, setupIntentId } =
        await subscriptionsApi.createTrialSetupIntent();

      // Шаг 2: подтверждаем карту через Stripe.js — данные карты не касаются нашего сервера.
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Card element not mounted");

      const { error: stripeError } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardElement } },
      );

      if (stripeError) {
        setError(
          stripeError.message ?? t("subscription.cardGate.errorGeneric"),
        );
        return;
      }

      // Шаг 3: серверная верификация + застолбить fingerprint карты (может вернуть
      // 409 TRIAL_CARD_ALREADY_USED — карта уже использовалась для trial).
      await subscriptionsApi.activateTrial(setupIntentId);

      onActivated();
    } catch (err: unknown) {
      if (extractApiErrorCode(err) === TRIAL_CARD_ALREADY_USED_CODE) {
        setAlreadyUsed(true);
        return;
      }
      const msg =
        err instanceof Error
          ? err.message
          : t("subscription.cardGate.errorGeneric");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const goToSubscription = () => {
    // Закрываем gate (исходный publish отклонится штатно) и уводим на выбор плана.
    onCancel?.();
    navigate("/settings/subscription");
  };

  if (alreadyUsed) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t("subscription.cardGate.alreadyUsedTitle")}
          </Typography>
          {t("subscription.cardGate.alreadyUsed")}
        </Alert>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" fullWidth>
              {t("subscription.cardGate.cancel")}
            </Button>
          )}
          <Button onClick={goToSubscription} variant="contained" fullWidth>
            {t("subscription.cardGate.goToSubscription")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Box sx={{ mb: 3 }}>
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: "16px",
                color: "#1a1a2e",
                "::placeholder": { color: "#aab7c4" },
              },
              invalid: { color: "#e53e3e" },
            },
          }}
          onChange={(e) => setCardComplete(e.complete)}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outlined"
            fullWidth
            size="large"
            disabled={loading}
          >
            {t("subscription.cardGate.cancel")}
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !stripe || !cardComplete}
          startIcon={loading ? <CircularProgress size={18} /> : <LockIcon />}
        >
          {loading
            ? t("subscription.cardGate.verifying")
            : t("subscription.cardGate.submit")}
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        textAlign="center"
      >
        {t("subscription.cardGate.noCharge")}
      </Typography>
    </Box>
  );
}

interface TrialCardGateProps extends TrialCardFormProps {
  /** Показывать ли шапку (иконка + заголовок + подзаголовок). По умолчанию — да. */
  showHeader?: boolean;
}

// ─────────────────────────────────────────────
// Card-gate: Stripe Elements + форма + деградация без ключа.
// Используется и модалкой (CardGateProvider), и standalone-страницей ниже.
// ─────────────────────────────────────────────
export function TrialCardGate({
  onActivated,
  onCancel,
  showHeader = true,
}: TrialCardGateProps) {
  const { t } = useTranslation("common");

  if (!stripePromise) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: onCancel ? 2 : 0 }}>
          {t("subscription.cardGate.stripeNotConfigured")}
        </Alert>
        {onCancel && (
          <Button onClick={onCancel} variant="outlined" fullWidth>
            {t("subscription.cardGate.cancel")}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {showHeader && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <CreditCardIcon color="primary" fontSize="large" />
            <Typography variant="h6" fontWeight={700}>
              {t("subscription.cardGate.title")}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {t("subscription.cardGate.subtitle")}
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      <Elements stripe={stripePromise}>
        <TrialCardForm
          onActivated={onActivated}
          {...(onCancel ? { onCancel } : {})}
        />
      </Elements>

      <Typography
        variant="caption"
        color="text.disabled"
        display="block"
        textAlign="center"
        sx={{ mt: 2 }}
      >
        {t("subscription.cardGate.securedByStripe")}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Standalone-страница (роут-запасной вариант проактивной активации trial).
// card-gate-поток при создании события идёт через модалку CardGateProvider.
// ─────────────────────────────────────────────
export function TrialActivation() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const benefits = [
    t("subscription.cardGate.benefitEvents"),
    t("subscription.cardGate.benefitDays"),
    t("subscription.cardGate.benefitNoCharge"),
    t("subscription.cardGate.benefitCancel"),
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ maxWidth: 480, width: "100%", p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <CreditCardIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight={700}>
            {t("subscription.cardGate.pageTitle")}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("subscription.cardGate.pageSubtitle")}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box component="ul" sx={{ pl: 2.5, mb: 3, "& li": { mb: 0.5 } }}>
          {benefits.map((item) => (
            <Typography key={item} component="li" variant="body2">
              {item}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <TrialCardGate
          showHeader={false}
          onActivated={() =>
            navigate("/dashboard", { state: { trialActivated: true } })
          }
        />
      </Paper>
    </Box>
  );
}

export default TrialActivation;
